import { create } from "zustand";

export interface IEnvironment {
	id: string;
	name: string;
	secret: string;
	token: string;
	origin: string;
	createdAt: string;
	updatedAt: string;
	createdBy: string;
	updatedBy: string;
}

export interface IEnvironmentFormData {
	name: string;
	origin: string;
}

export interface IEnvironmentFormErrors {
	name: string;
	origin: string;
}

type Environment = IEnvironment | null;

interface EnvironmentState {
	environmentToDelete: Environment;
	environmentToEdit: Environment;
	environmentToRotate: Environment;
	isDeleteModalOpen: boolean;
	isEditModalOpen: boolean;
	isRotateModalOpen: boolean;
	environments: IEnvironment[];
	isLoading: boolean;
	error: string | null;
	showTokenCopied: string | null;
	showSecretCopied: string | null;
	formData: IEnvironmentFormData;
	selectedEnvironmentId: string | null;
	selectedEnvironmentSecret: string | null;
	formErrors: IEnvironmentFormErrors;
	showToken: boolean;
	showSecret: boolean;
	setEnvironmentToDelete: (environment: Environment) => void;
	setEnvironmentToEdit: (environment: Environment) => void;
	setEnvironmentToRotate: (environment: Environment) => void;
	setIsDeleteModalOpen: (isOpen: boolean) => void;
	setIsEditModalOpen: (isOpen: boolean) => void;
	setIsRotateModalOpen: (isOpen: boolean) => void;
	setEnvironments: (environments: IEnvironment[]) => void;
	addEnvironment: (environment: IEnvironment) => void;
	updateEnvironment: (updatedEnvironment: IEnvironment) => void;
	deleteEnvironment: (environmentId: string) => void;
	setLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
	setShowTokenCopied: (id: string | null) => void;
	setShowSecretCopied: (id: string | null) => void;
	setFormData: (data: IEnvironmentFormData) => void;
	updateFormData: (field: keyof IEnvironmentFormData, value: string) => void;
	resetFormData: () => void;
	setSelectedEnvironmentId: (id: string | null) => void;
	setSelectedEnvironmentSecret: (secret: string | null) => void;
	setFormErrors: (errors: IEnvironmentFormErrors) => void;
	updateFormError: (field: keyof IEnvironmentFormErrors, error: string) => void;
	resetFormErrors: () => void;
	setShowToken: (show: boolean) => void;
	setShowSecret: (show: boolean) => void;
	toggleShowToken: () => void;
	toggleShowSecret: () => void;
}

export const useEnvironmentStore = create<EnvironmentState>((set) => ({
	environmentToDelete: null,
	environmentToEdit: null,
	environmentToRotate: null,
	isDeleteModalOpen: false,
	isEditModalOpen: false,
	isRotateModalOpen: false,
	environments: [],
	isLoading: false,
	error: null,
	showTokenCopied: null,
	showSecretCopied: null,
	formData: {
		name: "",
		origin: ""
	},
	selectedEnvironmentId: null,
	selectedEnvironmentSecret: null,
	formErrors: {
		name: "",
		origin: ""
	},
	showToken: false,
	showSecret: false,
	setEnvironmentToDelete: (environment) =>
		set({ environmentToDelete: environment }),
	setEnvironmentToEdit: (environment) =>
		set({ environmentToEdit: environment }),
	setEnvironmentToRotate: (environment) =>
		set({ environmentToRotate: environment }),
	setIsDeleteModalOpen: (isOpen) => set({ isDeleteModalOpen: isOpen }),
	setIsEditModalOpen: (isOpen) => set({ isEditModalOpen: isOpen }),
	setIsRotateModalOpen: (isOpen) => set({ isRotateModalOpen: isOpen }),
	setEnvironments: (environments) => set({ environments }),
	addEnvironment: (environment) =>
		set((state) => ({
			environments: [...state.environments, environment]
		})),
	updateEnvironment: (updatedEnvironment) =>
		set((state) => ({
			environments: state.environments.map((env) =>
				env.id === updatedEnvironment.id ? updatedEnvironment : env
			)
		})),
	deleteEnvironment: (environmentId) =>
		set((state) => ({
			environments: state.environments.filter((env) => env.id !== environmentId)
		})),
	setLoading: (isLoading) => set({ isLoading }),
	setError: (error) => set({ error }),
	setShowTokenCopied: (id) => set({ showTokenCopied: id }),
	setShowSecretCopied: (id) => set({ showSecretCopied: id }),
	setFormData: (data) => set({ formData: data }),
	updateFormData: (field, value) =>
		set((state) => ({
			formData: { ...state.formData, [field]: value }
		})),
	resetFormData: () =>
		set({
			formData: { name: "", origin: "" }
		}),
	setSelectedEnvironmentId: (id) => set({ selectedEnvironmentId: id }),
	setSelectedEnvironmentSecret: (secret) =>
		set({ selectedEnvironmentSecret: secret }),
	setFormErrors: (errors) => set({ formErrors: errors }),
	updateFormError: (field, error) =>
		set((state) => ({
			formErrors: { ...state.formErrors, [field]: error }
		})),
	resetFormErrors: () =>
		set({
			formErrors: { name: "", origin: "" }
		}),
	setShowToken: (show) => set({ showToken: show }),
	setShowSecret: (show) => set({ showSecret: show }),
	toggleShowToken: () => set((state) => ({ showToken: !state.showToken })),
	toggleShowSecret: () => set((state) => ({ showSecret: !state.showSecret }))
}));
